/************************************************************************************************
 * Copyright TRUSST AI PTY LTD. All Rights Reserved.                                            *
 *                                                                                              *
 * Licensed under the TRUSST SOFTWARE LICENSE (the "License"). You may not use this file except *
 * in compliance with the "LICENSE" file accompanying this file. This file is distributed       *
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied.       *
 *                                                                                              *
 * See the "License" file for the specific language governing permissions and limitations       *
 * under the License and limitations under the License.                                         *
 ***********************************************************************************************/

import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import SettingsApplicationsIcon from '@mui/icons-material/SettingsApplications';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Snackbar,
  Stack,
  TextField,
} from '@mui/material';
import {
  type GridFilterModel,
  GridRowGroupingModel,
  type GridSlotProps,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarFilterButton,
} from '@mui/x-data-grid-premium';
import React, {useState} from 'react';
import {type SavedSuperTableFilter, SuperTableFilters} from '../../services/super-table-filters';

declare module '@mui/x-data-grid-premium' {
  interface ToolbarPropsOverrides {
    setFilterButtonEl: React.Dispatch<React.SetStateAction<HTMLButtonElement | null>>;
    onApply: () => void;
    onClear: () => void;
    isPendingApply: boolean;
    userId: string;
    tableId: string;
    filterModel: GridFilterModel | undefined;
    onLoadFilter: (filter: GridFilterModel, groupingModel: GridRowGroupingModel) => void;
    groupingModel: GridRowGroupingModel;
    onToggleFullscreen: () => void;
    isFullscreen: boolean;
    onOpenOptions: () => void;
    isPartiallyLoading?: boolean;
  }
}

export function SuperTableToolbar({
  onApply,
  onClear,
  isPendingApply,
  setFilterButtonEl,
  userId,
  tableId,
  filterModel,
  onLoadFilter,
  groupingModel,
  onToggleFullscreen,
  isFullscreen,
  onOpenOptions,
  isPartiallyLoading = false,
}: GridSlotProps['toolbar'] & {
  onToggleFullscreen: () => void;
  isFullscreen: boolean;
  onOpenOptions: () => void;
  isPartiallyLoading?: boolean;
}) {
  const [loadDialogOpen, setLoadDialogOpen] = useState(false);
  const [savedFilters, setSavedFilters] = useState<SavedSuperTableFilter[]>([]);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [filterName, setFilterName] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [filterToDelete, setFilterToDelete] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const refreshFilters = async () => {
    try {
      const saved = await SuperTableFilters.loadFilters(userId, tableId);
      setSavedFilters(saved);
    } catch {
      console.error('Failed to load saved filters.');
    }
  };

  const handleSaveClick = async () => {
    await refreshFilters();
    if (savedFilters.length >= 10) {
      setSnackbarMessage('Maximum of 10 filters allowed. Please delete one before saving a new filter.');
      setSnackbarOpen(true);
      return;
    }
    setFilterName('');
    setSaveDialogOpen(true);
  };

  const handleSaveConfirm = async () => {
    if (!filterName || filterName.length > 50) return;
    if (!filterModel) return;
    const newFilter: SavedSuperTableFilter = {
      name: filterName,
      tableId,
      userId,
      criteria: filterModel,
      groupingModel,
    };
    try {
      await SuperTableFilters.saveFilter(newFilter);
      await refreshFilters();
      setSaveDialogOpen(false);
      setFilterName('');
    } catch (e: any) {
      console.error(e.message || 'Failed to save filter.');
    }
  };

  const handleLoadClick = async () => {
    await refreshFilters();
    setLoadDialogOpen(true);
  };

  const handleLoad = (filter: SavedSuperTableFilter) => {
    onLoadFilter(filter.criteria, filter.groupingModel);
    setLoadDialogOpen(false);
  };

  const handleDeletePrompt = (name: string) => {
    setFilterToDelete(name);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!filterToDelete) return;
    try {
      await SuperTableFilters.deleteFilter(userId, tableId, filterToDelete);
      await refreshFilters();
    } catch {
      console.error('Failed to delete filter.');
    } finally {
      setDeleteDialogOpen(false);
      setFilterToDelete(null);
    }
  };

  return (
    <>
      <GridToolbarContainer sx={{display: 'flex', justifyContent: 'space-between'}}>
        <Stack spacing={1} direction={'row'}>
          <GridToolbarFilterButton ref={setFilterButtonEl} />
          <Button
            size={'small'}
            onClick={onApply}
            variant={isPendingApply ? 'outlined' : 'text'}
            color={'primary'}
            disabled={isPartiallyLoading}
            sx={isPendingApply ? {color: 'var(--mui-palette-primary-main) !important'} : undefined}
          >
            Apply
          </Button>
          <Button size={'small'} onClick={onClear} disabled={isPartiallyLoading}>
            Reset
          </Button>
          <Divider orientation={'vertical'} flexItem variant={'middle'} />
          <Button size={'small'} onClick={handleLoadClick} disabled={isPartiallyLoading}>
            Load
          </Button>
          <Button size={'small'} onClick={handleSaveClick} disabled={isPartiallyLoading}>
            Save
          </Button>
        </Stack>
        <Stack spacing={1} direction={'row'}>
          <Button
            size={'small'}
            onClick={onOpenOptions}
            startIcon={<SettingsApplicationsIcon />}
            disabled={isPartiallyLoading}
          >
            Options
          </Button>
          <GridToolbarColumnsButton />
          <GridToolbarDensitySelector />
          <Divider orientation={'vertical'} flexItem variant={'middle'} />
          <GridToolbarExport
            printOptions={{disableToolbarButton: true}}
            slotProps={{button: {disabled: isPartiallyLoading}}}
          />
          <IconButton size={'small'} onClick={onToggleFullscreen} disabled={isPartiallyLoading}>
            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
        </Stack>
      </GridToolbarContainer>
      <Dialog open={loadDialogOpen} onClose={() => setLoadDialogOpen(false)}>
        <DialogTitle>Saved Filters</DialogTitle>
        <DialogContent sx={{width: 360}}>
          {savedFilters.length === 0 ? (
            <Box>No saved filters</Box>
          ) : (
            <List
              sx={
                {
                  // marginLeft: -2,
                }
              }
            >
              {savedFilters.map((filter) => (
                <ListItem
                  key={filter.name}
                  disablePadding
                  secondaryAction={
                    <IconButton edge={'end'} onClick={() => handleDeletePrompt(filter.name)}>
                      <CloseIcon color={'error'} />
                    </IconButton>
                  }
                >
                  <ListItemButton onClick={() => handleLoad(filter)}>
                    <ListItemText primary={filter.name} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLoadDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)}>
        <DialogTitle>Save Filter</DialogTitle>
        <DialogContent sx={{minWidth: 360}}>
          <TextField
            autoFocus
            margin={'dense'}
            label={'Filter Name'}
            fullWidth
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
            error={filterName.length > 50}
            helperText={filterName.length > 50 ? 'Max length is 50 characters' : ''}
            inputProps={{maxLength: 50}}
            required
          />
          <Stack direction={'row'} spacing={2} justifyContent={'flex-end'} sx={{mt: 2}}>
            <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveConfirm} disabled={!filterName || filterName.length > 50}>
              Save
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Filter</DialogTitle>
        <DialogContent sx={{minWidth: 360}}>
          <Box>Delete filter "{filterToDelete}"?</Box>
          <Stack direction={'row'} spacing={2} justifyContent={'flex-end'} sx={{mt: 2}}>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button color={'error'} onClick={handleDeleteConfirm}>
              Delete
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </>
  );
}
